/**
 * External dependencies
 */
import clsx from "clsx";

/**
 * WordPress dependencies
 */
import {
  RichText,
  useBlockProps,
  __experimentalGetBorderClassesAndStyles as getBorderClassesAndStyles,
  __experimentalGetColorClassesAndStyles as getColorClassesAndStyles,
  __experimentalGetSpacingClassesAndStyles as getSpacingClassesAndStyles,
  __experimentalGetShadowClassesAndStyles as getShadowClassesAndStyles,
  __experimentalGetElementClassName,
  getTypographyClassesAndStyles,
} from "@wordpress/block-editor";

export default function Save({ attributes, className }) {
  const {
    tagName,
    type,
    textAlign,
    fontSize,
    linkTarget,
    rel,
    style,
    text,
    title,
    url,
    width,
    icon,
    showText,
    animation,
    blockAlignment,
  } = attributes;

  const TagName = tagName || "a";
  const isButtonTag = "button" === TagName;
  const buttonType = type || "button";
  const borderProps = getBorderClassesAndStyles(attributes);
  const colorProps = getColorClassesAndStyles(attributes);
  const spacingProps = getSpacingClassesAndStyles(attributes);
  const shadowProps = getShadowClassesAndStyles(attributes);
  const typographyProps = getTypographyClassesAndStyles(attributes);

  let isOutlineButton =
    attributes &&
    attributes.className &&
    attributes.className.includes("is-style-outline");

  const buttonClasses = clsx(
    "wp-block-button__link",
    colorProps.className,
    borderProps.className,
    typographyProps.className,
    {
      [`has-text-align-${textAlign}`]: textAlign,
      // For backwards compatibility add style that isn't provided via
      // block support.
      "no-border-radius": style?.border?.radius === 0,
      [`has-custom-font-size`]: fontSize || style?.typography?.fontSize,
      [`has-animation-${animation}`]: animation && animation !== "none",
    },
    __experimentalGetElementClassName("button")
  );

  const buttonStyle = {
    ...borderProps.style,
    ...colorProps.style,
    ...spacingProps.style,
    ...shadowProps.style,
    ...typographyProps.style,
    writingMode: undefined,
    gap: icon?.enabled && icon?.gap && showText !== false ? icon.gap : undefined,
  };

  // Generate icon HTML from icon attributes
  const generateIconHtml = (icon) => {
    if (!icon?.enabled || !icon?.name || !icon?.type) {
      return "";
    }

    const iconClass = `ph${icon.type !== "regular" ? "-" + icon.type : ""} ph-${
      icon.name
    }`;

    return `<span class="brandy-button-icon-content" style="color:inherit"><i class="${iconClass}"></i></span>`;
  };

  const wrapperClasses = clsx(
    className,
    "wp-block-button wp-block-brandy-button",
    isOutlineButton ? "is-style-outline--2" : "",
    {
      [`has-custom-width wp-block-brandy-button__width-${width}`]: width,
    },
    blockAlignment ? `has-block-alignment-${blockAlignment}` : ""
  );

  const showIcon = icon?.enabled && icon?.name;
  const iconPosition = icon?.position || "left";

  return (
    <div {...useBlockProps.save({ className: wrapperClasses })}>
      {isButtonTag ? (
        <button
          type={buttonType}
          className={buttonClasses}
          style={buttonStyle}
          title={title}
        >
          {showIcon && iconPosition === "left" && (
            <span
              className="brandy-button-icon"
              dangerouslySetInnerHTML={{
                __html: generateIconHtml(icon),
              }}
            />
          )}
          {showText !== false && (
            <RichText.Content
              tagName="span"
              className="brandy-button-text"
              value={text}
            />
          )}
          {showIcon && iconPosition === "right" && (
            <span
              className="brandy-button-icon"
              dangerouslySetInnerHTML={{
                __html: generateIconHtml(icon),
              }}
            />
          )}
        </button>
      ) : (
        <a
          className={buttonClasses}
          href={url}
          title={title}
          style={buttonStyle}
          target={linkTarget}
          rel={rel}
        >
          {showIcon && iconPosition === "left" && (
            <span
              className="brandy-button-icon"
              dangerouslySetInnerHTML={{
                __html: generateIconHtml(icon),
              }}
            />
          )}
          {showText !== false && (
            <RichText.Content
              tagName="span"
              className="brandy-button-text"
              value={text}
            />
          )}
          {showIcon && iconPosition === "right" && (
            <span
              className="brandy-button-icon"
              dangerouslySetInnerHTML={{
                __html: generateIconHtml(icon),
              }}
            />
          )}
        </a>
      )}
    </div>
  );
}
